import * as PIXI from 'pixi.js';
import { AnimationConfig, FrameConfig } from '@/types/uiTypes';
import type { AnimatedSprite } from 'pixi.js';
import { EVENTS } from '@/utils/constants';
import type { Sprite } from 'pixi.js';
import { GameConfigs } from '@/types/gameConfig';

export class Frame extends PIXI.Sprite {
	ChildInstance: typeof Frame;
	pixiInstance: typeof PIXI;
	config: FrameConfig;
	sprites: AnimatedSprite[] = [];
	components: Record<string, Sprite> = {};
	GameConfigs: Partial<GameConfigs>;

	constructor(
		config: FrameConfig,
		pixiInstance: typeof PIXI,
		GameConfigs: Partial<GameConfigs>,
		childInstance: typeof Frame
	) {
		super();

		this.config = config;
		this.pixiInstance = pixiInstance;
		this.GameConfigs = GameConfigs;
		this.ChildInstance = childInstance;

		window.addEventListener(
			EVENTS.SETTINGS_UPDATE,
			this.settingsChanged.bind(this) as EventListener,
			false
		);
	}

	clickHandler(name: string) {
		if (this.config.handler) {
			this.config.handler(name);
		}
	}

	createVisualComponent(obj: FrameConfig) {
		// var vc = new VisualComponent(obj.component);
		// vc.name = obj.name;
		// this.addComponent(obj, vc);

		if (!obj.component) return;

		const vc = new this.ChildInstance(
			obj.component,
			this.pixiInstance,
			this.GameConfigs,
			this.ChildInstance
		);
		// const vc = new VisualComponent(obj.component);
		vc.name = obj.name;
		this.addComponent(obj, vc);
	}

	addComponent(obj: FrameConfig, component: Sprite) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		// FIXME: dynamic object should push in this.components variable but lot of change need for this implementation
		// temporary stay here ts-ignore
		this[obj.name] = component;
		if (obj.position) {
			component.position.set(obj.position.x, obj.position.y);
		}
		if (obj.scale) {
			component.scale.set(obj.scale.x, obj.scale.y);
		}
		if (obj.anchor) {
			component.anchor.set(obj.anchor.x, obj.anchor.y);
		}

		if (obj.components?.length) {
			const vc = new this.ChildInstance(
				obj,
				this.pixiInstance,
				this.GameConfigs,
				this.ChildInstance
			);
			if (obj.position) {
				vc.position.set(obj.position.x, obj.position.y);
			}

			component.position.set(0, 0);
			component.addChild(vc);
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			this[obj.name] = vc;
		}

		this.addChild(component);
	}

	createAnimatedSprite(obj: FrameConfig) {
		if (!obj.animation) return;

		const anim = this.getAnimatedSprite(obj);
		if (!this.GameConfigs.settings?.game_animation) {
			if (obj.animation.animatable === undefined) anim.gotoAndStop(0);
		} else {
			anim.play();
		}
		if (obj.animation.rotation) {
			anim.rotation = obj.animation.rotation;
		}
		if (obj.animation.anchor) {
			anim.anchor.set(obj.animation.anchor.x, obj.animation.anchor.y);
		}
		anim.name = obj.name;
		if (obj.animation.animatable === undefined) this.sprites.push(anim);
		this.addComponent(obj, anim);
	}

	getAnimatedSprite(obj: FrameConfig) {
		if (!obj.animation)
			return new this.pixiInstance.AnimatedSprite([PIXI.Texture.EMPTY]);

		const pattern = obj.animation?.pattern?.split('$') || [];
		const textureArray: PIXI.Texture[] = this.animate(
			obj.animation as AnimationConfig,
			pattern
		);

		const anim = new this.pixiInstance.AnimatedSprite(textureArray);
		anim.loop = obj.animation.loop || false;
		anim.animationSpeed = obj.animation?.speed || 1;

		if (obj.animation?.scale) {
			anim.scale.x = obj.animation.scale.x || 1;
			anim.scale.y = obj.animation.scale.y || 1;
		}

		if (obj.animation?.anchor) {
			anim.anchor.set(obj.animation.anchor.x, obj.animation.anchor.y);
		}

		if (obj.animation?.rotation) {
			anim.rotation = obj.animation.rotation;
		}

		return anim;
	}

	animate(obj: AnimationConfig, pattern: string[], arr?: unknown[]) {
		const textureArr = [];
		for (let i = obj.start; i <= obj.end; i += 1) {
			const current =
				pattern[0] + String(i).padStart(obj.precision, '0') + pattern[1];
			const texture = this.pixiInstance.Texture.from(current);
			textureArr.push(texture);
			if (arr) arr.push(texture);
		}

		return textureArr;
	}

	settingsChanged(e: CustomEvent) {
		if (e.detail.data.game_animation === undefined) {
			return;
		}

		for (let i = 0; i < this.sprites.length; i++) {
			if (!e.detail.data.game_animation) {
				this.sprites[i].gotoAndStop(0);
			} else {
				this.sprites[i].gotoAndPlay(0);
			}
		}
	}

	resize(w: number, h: number) {
		if (this.config.controllers !== undefined) {
			for (let i = 0; i < this.config.controllers.length; i++) {
				const element = this.config.controllers[i];
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				if (this[element.name].resize) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					this[element.name].resize(w, h);
				}
			}
		}
	}
}
